import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import urlModule from 'url';
import { useMemo, useRef } from 'react';
import { useLocale } from './i18n/useLocale';
import { EMBEDDED_CONFLUENCE_MODE } from './EmbeddedConfluenceMode';
import { ALLOWED_FEATURES } from '../features';
import { themeObjectToString } from '@atlaskit/tokens';
export var themeStateObjectToQueryString = function themeStateObjectToQueryString(themeState) {
  var themeString = themeObjectToString(themeState);
  return encodeURIComponent(themeString);
};
var IN_EDITOR_TEMPLATES_QUERY_AUTO_CLOSED = 'auto_closed';
export var useUrl = function useUrl(_ref) {
  var addTemplateBrowserQuery = _ref.addTemplateBrowserQuery,
    allowedFeatures = _ref.allowedFeatures,
    contentId = _ref.contentId,
    hash = _ref.hash,
    hostname = _ref.hostname,
    intl = _ref.intl,
    locale = _ref.locale,
    mode = _ref.mode,
    parentProduct = _ref.parentProduct,
    parentProductContentContainerId = _ref.parentProductContentContainerId,
    parentProductContentId = _ref.parentProductContentId,
    protocol = _ref.protocol,
    spaceKey = _ref.spaceKey,
    themeMode = _ref.themeMode,
    themeState = _ref.themeState;
  var _useLocale = useLocale({
      locale: locale,
      intl: intl
    }),
    formattedLocale = _useLocale.locale;
  var initialAllowedFeaturesRef = useRef(undefined);

  // Keep `initialAllowedFeatures` to only contain the initial allowed feature values for cc-frontend SSR rendering
  // Store this value using `useRef` to avoid the iframe url gets updated when `allowedFeatures` React props updated by parent products.
  // If allowedFeatures is equal to 'all' for view mode it must be converted to an array so that 'edit' can be removed.
  var features = allowedFeatures === 'all' ? _toConsumableArray(ALLOWED_FEATURES[mode]) : allowedFeatures;
  initialAllowedFeaturesRef.current = Array.isArray(features) ? features.map(function (feature) {
    if (typeof feature !== 'string') {
      return JSON.stringify(feature);
    } else {
      return feature;
    }
  })
  // 'edit' can not be included as an initialAllowedFeature because it requires the navigation policy, which does not work with SSR.
  .filter(function (feature) {
    return feature !== 'edit';
  }).join('.') : features;
  var queryParams = useMemo(function () {
    var query = {};
    if (parentProduct) {
      query.parentProduct = parentProduct;
    }
    if (parentProductContentContainerId) {
      query.parentProductContentContainerId = parentProductContentContainerId;
    }
    if (parentProductContentId) {
      query.parentProductContentId = parentProductContentId;
    }
    if (addTemplateBrowserQuery) {
      // 'auto_closed' makes the initial state closed if there is content, if no content, the panel will open and query param will auto switch to 'open'
      query.inEditorTemplatesPanel = IN_EDITOR_TEMPLATES_QUERY_AUTO_CLOSED;
    }
    if (initialAllowedFeaturesRef.current !== undefined) {
      query.initialAllowedFeatures = initialAllowedFeaturesRef.current;
    }
    if (themeMode) {
      query.themeMode = themeMode;
    }
    if (themeState) {
      query.themeState = themeStateObjectToQueryString(themeState);
    }
    if (hostname && hostname !== window.location.host) {
      query.isCrossDomain = true;
    }
    query.locale = formattedLocale;
    return urlModule.format({
      query: query
    });
  }, [addTemplateBrowserQuery, formattedLocale, parentProduct, parentProductContentContainerId, parentProductContentId, themeMode, themeState, hostname]);
  return useMemo(function () {
    return _objectSpread(_objectSpread({}, constructUrl(mode, {
      protocol: protocol,
      hostname: hostname,
      spaceKey: spaceKey,
      contentId: contentId,
      queryParams: queryParams,
      hash: hash
    })), {}, {
      formattedLocale: formattedLocale
    });
  }, [mode, protocol, hostname, spaceKey, contentId, queryParams, hash, formattedLocale]);
};
var CONTEXT_PATH = '/wiki/spaces';
var invalidUrls = {
  standaloneConfluenceUrl: null,
  embeddedConfluenceUrl: null
};
function constructUrl(mode, _ref2) {
  var contentId = _ref2.contentId,
    _ref2$hash = _ref2.hash,
    hash = _ref2$hash === void 0 ? '' : _ref2$hash,
    hostname = _ref2.hostname,
    protocol = _ref2.protocol,
    queryParams = _ref2.queryParams,
    spaceKey = _ref2.spaceKey;
  if (!contentId) {
    /* eslint-disable-next-line no-console */
    console.error('Content ID is undefined');
    return invalidUrls;
  }
  if (!spaceKey) {
    /* eslint-disable-next-line no-console */
    console.error('Space key is undefined');
    return invalidUrls;
  }
  var prefix = "".concat(protocol || window.location.protocol, "//").concat(hostname || window.location.host).concat(CONTEXT_PATH);
  var base;
  switch (mode) {
    case EMBEDDED_CONFLUENCE_MODE.VIEW_MODE:
      base = "".concat(spaceKey, "/pages/").concat(contentId);
      break;

    // Not exposed yet, will update readme after content migration
    case EMBEDDED_CONFLUENCE_MODE.EDIT_MODE:
      base = "".concat(spaceKey, "/pages/edit-embed/").concat(contentId);
      break;
    default:
      base = '';
  }
  if (!base) {
    /* eslint-disable-next-line no-console */
    console.error('URL is not yet supported');
    return invalidUrls;
  }
  var standaloneConfluenceUrl = "".concat(prefix, "/").concat(base).concat(hash);
  var embeddedConfluenceUrl = "".concat(prefix, "/").concat(base).concat(queryParams).concat(hash);
  return {
    standaloneConfluenceUrl: standaloneConfluenceUrl,
    embeddedConfluenceUrl: embeddedConfluenceUrl
  };
}